import { Ghost } from "phosphor-react"
import { useEffect } from "react"
import { isRouteErrorResponse, useRouteError } from "react-router-dom"
import * as allPaths from "~/common/paths"
import { useDocumentTitleDescription } from "~/common/use-document-title"
import logoLg from "~/images/logo-lg"
import { AppText } from "./app-text"
import { Button } from "./button"

// First path segment of every route defined in ~/common/paths. Used to
// distinguish `/:room_handle/...` from `/admin/...`, `/landing/...`, etc.
// INVARIANT: any top-level SPA path MUST be declared in paths.ts — otherwise
// `/that-path/foo` will be misclassified as a room handle and trigger a
// full-page redirect to `/that-path`.
const NON_ROOM_SEGMENTS = new Set(
  Object.values(allPaths)
    .map((p) => (p as { pattern?: string }).pattern?.split("/")[1])
    .filter((s): s is string => !!s && !s.startsWith(":"))
)

// If the URL looks like /:roomHandle/:anything, fall back to /:roomHandle
// preserving query + hash so the room page can handle the rest.
export function getRoomFallback(): string | null {
  const segments = window.location.pathname.split("/").filter(Boolean)
  if (segments.length >= 2 && !NON_ROOM_SEGMENTS.has(segments[0])) {
    return `/${segments[0]}${window.location.search}${window.location.hash}`
  }
  return null
}

const goHome = () => {
  window.location.href = "/"
}

type EmptyStateProps = {
  heading: string
  body?: string
  cta?: { label: string; onClick: () => void }
}

const EmptyState = ({ heading, body, cta }: EmptyStateProps) => (
  <>
    <Ghost size={80} weight="thin" className="text-base-gray" />
    <AppText variant="h3" className="mt-4 text-base-black">
      {heading}
    </AppText>
    {body ? (
      <AppText variant="body2" className="mt-2 text-base-gray">
        {body}
      </AppText>
    ) : null}
    {cta ? (
      <Button variant="primary" size="medium" className="mt-8" onClick={cta.onClick}>
        {cta.label}
      </Button>
    ) : null}
  </>
)

const NotFound = () => {
  const roomFallback = getRoomFallback()

  useEffect(() => {
    // replace() avoids adding a history entry, so Back doesn't loop on the bad URL.
    if (roomFallback) window.location.replace(roomFallback)
  }, [roomFallback])

  if (roomFallback) {
    return <EmptyState heading="Redirecting..." />
  }

  return (
    <EmptyState
      heading="Page not found"
      body="This link may be expired or incorrect."
      cta={{ label: "Go to ChatBCC", onClick: goHome }}
    />
  )
}

export const ErrorBoundary = () => {
  const error = useRouteError()
  useDocumentTitleDescription("Error", "An error occured")

  if (isRouteErrorResponse(error) && error.status === 404) {
    return (
      <ErrorPage>
        <NotFound />
      </ErrorPage>
    )
  }

  if (isRouteErrorResponse(error)) {
    return (
      <ErrorPage>
        <EmptyState
          heading={error.statusText || "Something went wrong"}
          cta={{ label: "Go to ChatBCC", onClick: goHome }}
        />
      </ErrorPage>
    )
  }

  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    // No Sentry on web yet (see hooks/use-phone-submission.ts) — log so devs
    // can still see the cause in the console when this screen shows up.
    console.error("ErrorBoundary caught:", error)
    return (
      <ErrorPage>
        <EmptyState
          heading="Something went wrong"
          body="Please try again or check back soon."
          cta={{ label: "Retry", onClick: () => location.reload() }}
        />
      </ErrorPage>
    )
  }

  throw error
}

const ErrorPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex min-h-screen flex-col bg-white">
    <header className="flex items-center px-6 py-4">
      <img src={logoLg.src} alt="ChatBCC" className="h-8" />
    </header>
    <div className="flex flex-1 flex-col items-center justify-center px-4 pb-16 text-center">
      {children}
    </div>
  </div>
)
